import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Link as LinkIcon, CheckCircle, XCircle } from 'lucide-react';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { usePmt3DaypartingData } from '@/hooks/usePmt3DaypartingData';
import { formatDayHourKey } from '@/utils/timeUtils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

const summaryItems = [
  'Enable Dayparting',
  'Use Farming Group Multipliers',
  'Use Global Multipliers',
  'Current Time',
  "Current Time's Multiplier",
  'Total Multipliers',
  'Positive Multipliers',
  'Negative Multipliers',
];

const DaypartingDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = usePmt3DaypartingData(selectedProduct?.companyName, selectedProduct?.id);
  const [clientUrl, setClientUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchUrl = async () => {
      if (!selectedProduct?.companyName) {
        setClientUrl(null);
        return;
      }
      try {
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', selectedProduct.companyName, 'items', 'Dayparting Bid Calculations');
        const urlSnap = await getDoc(urlDocRef);
        if (urlSnap.exists()) {
          const urlVal = urlSnap.data().value || urlSnap.data().url || null;
          setClientUrl(urlVal);
        } else {
          setClientUrl(null);
        }
      } catch (error) {
        console.error('Error fetching Dayparting Bid Calculations URL:', error);
        setClientUrl(null);
      }
    };
    fetchUrl();
  }, [selectedProduct?.companyName]);

  const highlightIfDifferent = (current: any, previous: any, formatter = (v: any) => v) => {
    if (previous === null || previous === undefined || previous === '' || previous === '-' || current === previous || String(current) === String(previous)) {
      return '-';
    }
    return (
      <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
        {formatter(previous)}
      </span>
    );
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return `${Math.round(value * 100)}%`;
  };

  return (
    <div className="w-[75%] mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center">
        Dayparting
        {clientUrl && (
          <a
            href={clientUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open Dayparting Bid Calculations"
            className="text-blue-500 hover:text-blue-700 inline-flex items-center ml-2"
          >
            <LinkIcon size={18} />
          </a>
        )}
      </h2>
      <div className="flex flex-col md:flex-row gap-4 items-start">
        <Card className="w-full md:w-3/5">
          <CardHeader className="p-4">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-semibold">Summary</CardTitle>
              {clientUrl && (
                <a
                  href={clientUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open Dayparting Bid Calculations"
                  className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
                >
                  <LinkIcon size={16} />
                </a>
              )}
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto p-4">
            {error && <div className="text-red-600 mb-4">{error.message}</div>}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="py-2 px-3">Item</TableHead>
                  <TableHead className="py-2 px-3">Value</TableHead>
                  <TableHead className="py-2 px-3">Old Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaryItems.map((item) => {
                  let value: any = '-';
                  let oldValue: any = '-';

                  if (data) {
                    switch (item) {
                      case 'Enable Dayparting':
                        value = data.enableDayparting ?? '-';
                        oldValue = data.oldValues['Enable Dayparting'] ?? '-';
                        break;
                      case 'Use Farming Group Multipliers':
                        value = data.useFarmingGroupMultipliers ?? '-';
                        oldValue = data.oldValues['Use Farming Group Multipliers'] ?? '-';
                        break;
                      case 'Use Global Multipliers':
                        value = data.useGlobalMultipliers ?? '-';
                        oldValue = data.oldValues['Use Global Multipliers'] ?? '-';
                        break;
                      case 'Current Time':
                        value = data.currentTimeKey ?? '-';
                        oldValue = '-';
                        break;
                      case "Current Time's Multiplier":
                        value = formatPercent(data.currentTimeMultiplier);
                        oldValue = '-';
                        break;
                      case 'Total Multipliers':
                        value = data.totalMultipliers;
                        oldValue = data.oldValues['Total Multipliers'] ?? '-';
                        break;
                      case 'Positive Multipliers':
                        value = data.positiveMultipliers;
                        oldValue = data.oldValues['Positive Multipliers'] ?? '-';
                        break;
                      case 'Negative Multipliers':
                        value = data.negativeMultipliers;
                        oldValue = data.oldValues['Negative Multipliers'] ?? '-';
                        break;
                    }
                  }

                  let colorClass = '';
                  let icon: React.ReactNode = null;

                  if (!isLoading && data) {
                    switch (item) {
                      case 'Enable Dayparting':
                      case 'Use Farming Group Multipliers':
                      case 'Use Global Multipliers':
                        if (value === 'Yes') {
                          colorClass = 'text-green-700';
                          icon = <CheckCircle size={16} className="mr-1" />;
                        } else if (value === 'No') {
                          colorClass = 'text-yellow-500';
                        }
                        break;
                      case "Current Time's Multiplier":
                        const multiplier = data.currentTimeMultiplier;
                        if (multiplier !== null && multiplier !== undefined) {
                          if (multiplier > 1) {
                            colorClass = 'text-green-700';
                            icon = <CheckCircle size={16} className="mr-1" />;
                          } else if (multiplier < 1) {
                            colorClass = 'text-red-600';
                            icon = <XCircle size={16} className="mr-1" />;
                          } else {
                            colorClass = 'text-yellow-500';
                          }
                        }
                        break;
                    }
                  }

                  return (
                    <TableRow key={item} className="py-1">
                      <TableCell className="py-1 px-3">{item}</TableCell>
                      <TableCell className={`py-1 px-3 ${colorClass}`}>
                        {isLoading ? (
                          <Skeleton className="h-4 w-16" />
                        ) : (
                          <div className="flex items-center">
                            {icon}
                            <span>{value}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1 px-3">
                        {isLoading ? <Skeleton className="h-4 w-16" /> : highlightIfDifferent(value, oldValue)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="w-full md:w-[35%]">
          <CardHeader className="p-4">
            <CardTitle className="text-lg font-semibold">All Multipliers</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-4">
            {error && !data && <div className="text-red-600 mb-4">{error.message}</div>}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="py-2 px-3">Day & Hour</TableHead>
                  <TableHead className="py-2 px-3">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 10 }).map((_, idx) => (
                      <TableRow key={idx} className="py-1">
                        <TableCell className="py-1 px-3">
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell className="py-1 px-3">
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                      </TableRow>
                    ))
                  : data?.allMultipliers.map((entry) => (
                      <TableRow key={entry.id} className="py-1">
                        <TableCell className="py-1 px-3">{formatDayHourKey(entry.id)}</TableCell>
                        <TableCell className="py-1 px-3">{formatPercent(entry.finalMultiplier)}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DaypartingDashboard;